import type { StorefrontPage } from "./storefrontTemplates.js";

/**
 * Pages built from a list of sections: each page's sections by id, with the ids of the variants
 * each is drawn in, the default first. The design system draws them (`HOME_SECTIONS`, held equal
 * to this by a test); the ids live here so a server can check a page's list without React, as it
 * will a store's own. `docs/design-system/templates.md` ("Sections and variants").
 */
export const STOREFRONT_SECTION_VARIANTS = {
  home: {
    hero: ["split", "centered", "carousel"],
    liveHits: ["strip", "cards"],
    recentOpens: ["compact", "cards"],
    featuredBoxes: ["grid", "large", "rail"],
    battles: ["grid", "rows"],
    forge: ["showcase", "band", "stage"],
    promoCards: ["row"],
    products: ["grid"],
    reviews: ["grid"],
    promises: ["grid"],
    faq: ["split", "centered", "stacked"],
    closing: ["band"],
  },
} as const satisfies {
  readonly [P in StorefrontPage]?: Readonly<Record<string, readonly string[]>>;
};

/** A page built from a list of sections. */
export type StorefrontListPage = keyof typeof STOREFRONT_SECTION_VARIANTS;

/** A section of a list page, by id: `"featuredBoxes"`. */
export type StorefrontSectionId<P extends StorefrontListPage> =
  keyof (typeof STOREFRONT_SECTION_VARIANTS)[P] & string;

/**
 * The layouts a page list can be drawn in, each with the slots it places sections in, in order.
 * `column`: `lead`, the top of the page (Home's hero, under a floating nav), then `body`, one
 * column below it. A page whose parts sit side by side (the box page) adds a layout of its own
 * with its own slots, so a stored list always says which slots it means.
 */
export const PAGE_LAYOUT_SLOTS = {
  column: ["lead", "body"],
} as const;
export type PageLayout = keyof typeof PAGE_LAYOUT_SLOTS;
export type PageLayoutSlot<L extends PageLayout> = (typeof PAGE_LAYOUT_SLOTS)[L][number];

/**
 * How far apart a page list sets its sections: `roomy` (a showroom's), `tight` (an arcade's).
 * Not the theme's `shape.density`, which sizes controls and padding everywhere.
 */
export const PAGE_SPACINGS = ["roomy", "tight"] as const;
export type PageSpacing = (typeof PAGE_SPACINGS)[number];

/** One section on a page: which, the variant it is drawn in, and the settings it changes. */
export interface PageSectionEntry<Id extends string = string> {
  readonly section: Id;
  /** Left out, the section's default (the first of its variants). */
  readonly variant?: string;
  readonly settings?: Readonly<Record<string, unknown>>;
}

/**
 * A page as an ordered list of sections in the slots of a layout, at a spacing: the shape a
 * template's preset is written in, and the one a store's own page will be stored in. A section
 * appears at most once; one it leaves out is not drawn, unless the page requires it
 * (`STOREFRONT_REQUIRED_SECTIONS`).
 */
export type PageSectionList<Id extends string = string> = {
  [L in PageLayout]: {
    readonly layout: L;
    readonly spacing: PageSpacing;
    readonly slots: { readonly [S in PageLayoutSlot<L>]: readonly PageSectionEntry<Id>[] };
  };
}[PageLayout];

/**
 * The sections a page's every template must draw, whatever its list: what the page does or must
 * say (its one h1, the opener, the odds), never merchandising. Home needs only its hero; every
 * other section is a preset's (and later a store's) to choose. A page that is not built from a
 * list yet draws every section its content has, so its entry here says what its list will keep.
 */
export const STOREFRONT_REQUIRED_SECTIONS: {
  readonly [P in StorefrontPage]?: readonly string[];
} = {
  home: ["hero"],
  box: ["heading", "opener", "contents"],
};
